import { takeFullPageSnapshot } from '@Services/snapShot/snapshot.service';
import { HttpService } from '@Services/index';
import { useBoundStore } from '@Store/index.ts';
import { bugsnagClient } from '@Services/observability/initObservability';
import { getTermsAndConditions } from './termsAndConditions';
import type { ConsentTypes } from './termsAndConditions';

interface UploadConestParams {
  type: ConsentTypes;
  fileName?: string;
  preExistingBlob?: Blob;
  nodes?: HTMLElement[];
}
export const uploadConsent = async ({
  type,
  fileName = 'Snapshot',
  preExistingBlob,
  nodes = [
    document.querySelector('#page-content') as HTMLElement,
    document.querySelector('#page-footer') as HTMLElement,
  ],
}: UploadConestParams): Promise<boolean> => {
  const { customerId } = useBoundStore.getState().customer;
  const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();

  return new Promise((res, rej) => {
    (async () => {
      const formData: FormData = new FormData();
      const snapShotBlob =
        preExistingBlob ??
        (await takeFullPageSnapshot({
          nodes,
        }));
      try {
        const consentResponse = await getTermsAndConditions(type);
        if (!snapShotBlob) return res(false);

        formData.append('image', snapShotBlob, fileName);
        formData.append(
          'consents',
          new Blob([JSON.stringify(consentResponse.termsAndConditions)], {
            type: 'application/json',
          }),
          'consents'
        );

        const url = isEmbedded
          ? `v3/secureonboarding/partner/user/${customerId}/terms_and_conditions/consent`
          : `v1/user/${customerId}/terms_and_conditions/consent`;

        await HttpService.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        res(true);
      } catch (error) {
        console.error('Error uploading snapshot', error);
        rej(false);
      }
    })();
  });
};

export enum POATypes {
  PLAN_SELECTION = 'PLANS_SELECTION',
  KYB = 'KYB',
  OWNERS_CENTER = 'OWNERS_CENTER',
}

interface UploadGenericSnapshotParams {
  fileUsageType: POATypes;
  dataFileName?: string;
  nodes?: HTMLElement[];
}
export const uploadGenericSnapshot = async ({
  fileUsageType,
  dataFileName = '',
  nodes = [
    document.querySelector('#page-content') as HTMLElement,
    document.querySelector('#page-footer') as HTMLElement,
  ],
}: UploadGenericSnapshotParams) => {
  try {
    const {
      customer: { customerId },
      onboardingSlice: { getIsEmbedded },
    } = useBoundStore.getState();
    const formData = new FormData();
    const imageBlob = await takeFullPageSnapshot({
      nodes,
    });

    if (!imageBlob) return false;
    const dataBlob = new Blob([
      JSON.stringify({
        fileUsageType: fileUsageType,
        fileName: dataFileName,
      }),
    ]);

    formData.append('image', imageBlob, Date.now().toString());
    formData.append('data', dataBlob);

    const uploadGenericImageUrl = getIsEmbedded()
      ? `v3/secureonboarding/partner/user/${customerId}/uploadImageFile`
      : `v1/user/${customerId}/uploadImageFile`;
    await HttpService.post(uploadGenericImageUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return true;
  } catch (e) {
    bugsnagClient.notify(`Error in uploading generic file: ${e}`);
    return false;
  }
};
